import React from "react"
import ThemeProvider from "@material-ui/styles/ThemeProvider"

import Layout from "../../../components/layout"
import SEO from "../../../components/seo"
import PracticeRoutinePost from "../../../components/practice-routine-post"
import theme from "../../../theme"

const summary = [
  {
    level: "Preliminary to Grade 2",
    time: "10 to 20 minutes",
    focus: "Breath, posture, gentle warm-ups, clear words, pitch matching",
    weeklyHabit: "Keep sessions short and comfortable, with plenty of listening",
  },
  {
    level: "Grades 3 to 4",
    time: "25 to 35 minutes",
    focus: "Breath control, diction, phrase shape, range building, simple sight singing",
    weeklyHabit: "Warm up gently and practise text, rhythm, and melody separately",
  },
  {
    level: "Grades 5 to 6",
    time: "35 to 50 minutes",
    focus: "Tone consistency, resonance, language, character, aural awareness",
    weeklyHabit: "Record phrases and check words, tuning, expression, and breath points",
  },
  {
    level: "Grades 7 to 8",
    time: "45 to 60 minutes",
    focus: "Style, stamina, interpretation, program contrast, performance presence",
    weeklyHabit: "Balance technical work with rest, text study, and mock performances",
  },
]

const sections = [
  {
    title: "Preliminary to Grade 2: keep the voice comfortable",
    paragraphs: [
      "Early singing practice should feel light and safe. Begin with posture, easy breathing, gentle humming, pitch matching, and simple songs in a comfortable range.",
      "Avoid long repetition. Short sessions with careful listening are more useful than pushing for volume or range.",
    ],
  },
  {
    title: "Grades 3 to 4: connect words and music",
    paragraphs: [
      "As practice grows, students can separate text, rhythm, and melody before putting them together. Diction, breath points, and phrase endings become part of the routine.",
      "Sight singing and aural work should appear several times a week. Even a few minutes of interval singing or rhythm reading helps the exam work feel less separate.",
    ],
  },
  {
    title: "Grades 5 to 6: build expressive control",
    paragraphs: [
      "Middle-grade singers need consistent tone, clear vowels, reliable breathing, and a stronger connection to text. Record short phrases and listen for tuning, words, and expression.",
      "If a song is in another language, include slow text practice away from the melody. Confidence often grows when the student understands the words and their shape.",
    ],
  },
  {
    title: "Grades 7 to 8: manage stamina and style",
    paragraphs: [
      "Advanced singers need a planned routine that protects the voice. Rotate technical warm-ups, repertoire detail, language, interpretation, sight singing, and performance practice.",
      "Mock performances help with program flow and stage confidence, but rest is part of the plan too. A tired voice needs recovery, not more force.",
    ],
  },
  {
    title: "A simple weekly rhythm",
    paragraphs: [
      "A calendar can separate vocal technique, repertoire, text study, aural work, recordings, rehearsals, and rest days.",
      "For singers, planning rest is not optional. A sustainable routine keeps the voice fresh enough for musical work.",
    ],
  },
]

const SingingPracticeRoutinesAmebLevels = () => (
  <ThemeProvider theme={theme}>
    <Layout>
      <SEO
        title="Singing Practice Schedule by AMEB Level"
        description="Plan singing practice for AMEB levels with healthy routines for warm-ups, diction, repertoire, sight singing, aural work, and performance confidence."
      />
      <PracticeRoutinePost
        instrument="Singing"
        title="Singing practice routines for different AMEB levels"
        intro="A good singing routine protects the voice while building breath, pitch, diction, expression, and confidence over time."
        sharedCalendarUrl="https://app.schoolcalendar.me/calendar/lazy-deer-51"
        image="blog-singing.png"
        imageAlt="Singing practice planning"
        summary={summary}
        sections={sections}
      />
    </Layout>
  </ThemeProvider>
)

export default SingingPracticeRoutinesAmebLevels
